/**
 * Saving a file to the reader's device, under a name they can find again.
 *
 * A "Download" control must save, not open: a contact's file card called
 * `window.open(file.fileUrl, "_blank")`, so the file opened in a new tab and
 * the reader was left to save it from there, under whatever name the browser
 * guessed (2026-10-08, alongside the same fix in PropPay LX). Files are
 * served from this app's own origin (`/public-objects/…`, `/api/…`), never a
 * storage provider's address — keep it that way: no user should ever see one.
 *
 * `client/src/lib/download.test.ts` fails on a file opened in a new tab where
 * it should be saved.
 *
 * EVERY FILE IS NAMED BY ONE RULE (shared/fileNames.ts, Andrew 2026-10-27):
 * `<Owner> - <Document> (<state>) - <Subject> - <YYYY-MM-DD>.<ext>`. Inside a
 * project the owner is the project's name and the day is the project's
 * (AppShell keeps both current, as it keeps the display zone); outside one it
 * is SaleFish. `projectFileName` is how a page names what it hands out.
 */
import { fileName, type FileNameParts } from "@shared/fileNames";
import { getDisplayTimeZone } from "@/lib/format";

/** Who owns what the app hands out right now: the open project, or SaleFish outside one. */
let owner = "SaleFish";

/** Set by AppShell from the open project (useProjectTimeZoneSync); null outside a project. */
export function setFileOwner(name: string | null | undefined): void {
  owner = name?.trim() || "SaleFish";
}

export function fileOwner(): string {
  return owner;
}

/**
 * The standard name for a file the open project hands out: its name, the
 * document's title, a state and subjects when they tell copies apart, and the
 * document's own day (today for an export) in the project's zone. Pass
 * `owner: "SaleFish"` for anything wider than one project.
 */
export function projectFileName(p: Omit<FileNameParts, "owner" | "date" | "timeZone"> & { owner?: string; date?: Date | string; timeZone?: string }): string {
  return fileName({ ...p, owner: p.owner ?? owner, date: p.date ?? new Date(), timeZone: p.timeZone ?? getDisplayTimeZone() });
}

/**
 * Save `blob` as `filename`. Attached, clicked, removed: an anchor that is
 * never in the document does nothing in Firefox. The object URL is released a
 * moment later rather than in the same tick, which some WebKit builds need
 * before the download has read it.
 */
export function saveBlob(blob: Blob, filename: string): void {
  if (typeof document === "undefined") return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  try {
    a.click();
  } finally {
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

/**
 * The name a response's Content-Disposition gives its file: the RFC 5987
 * `filename*` when there is one (it carries every script), else `filename`.
 */
export function nameFromDisposition(header: string | null | undefined): string | null {
  if (!header) return null;
  const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      /* fall through to the plain name */
    }
  }
  const plain = /filename\s*=\s*"([^"]*)"/i.exec(header) ?? /filename\s*=\s*([^;]+)/i.exec(header);
  return plain?.[1]?.trim() || null;
}

/**
 * Fetch `url` (with this app's session) and save it as `filename`. Fetched
 * rather than linked, so the name holds whatever the URL turns out to be —
 * `download` is ignored on a cross-origin link. Without `filename`, the name
 * the server gave it. Throws when the file can't be fetched; the caller says
 * so, and must not fall back to opening the link.
 */
export async function downloadUrl(url: string, filename?: string): Promise<void> {
  const res = await fetch(url, { credentials: "same-origin" });
  if (!res.ok) throw new Error(`download failed: ${res.status}`);
  saveBlob(await res.blob(), filename ?? nameFromDisposition(res.headers.get("content-disposition")) ?? fileNameFor(url.split("?")[0].split("/").pop() ?? ""));
}

/** Characters no desktop or phone file system accepts in a name. */
const UNSAFE = /[\\/:*?"<>|\u0000-\u001f]+/g;

/** A name made safe to save under, keeping (or adding) its extension. */
export function fileNameFor(name: string, ext?: string): string {
  const base = name.replace(UNSAFE, "-").replace(/\s+/g, " ").trim().replace(/^[.\s-]+|[.\s-]+$/g, "") || "Document";
  if (!ext) return base;
  const dot = ext.startsWith(".") ? ext : `.${ext}`;
  return base.toLowerCase().endsWith(dot.toLowerCase()) ? base : `${base}${dot}`;
}

/**
 * A Document Library file, named so a folder of them sorts and reads:
 * `SaleFish - Terms of Service - v1.0 - 2026-01-01.pdf`. `updated` is the
 * document's own YYYY-MM-DD, used as written (no timezone shift).
 */
export function libraryFileName(title: string, version: string, updated: string): string {
  return fileName({ owner: "SaleFish", document: title, subject: [`v${version}`], date: updated, ext: "pdf" });
}
